import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Grid,
  CircularProgress,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  TextField
} from '@mui/material';
import { IconDownload, IconClock, IconUserCheck, IconArrowRight, IconRefresh } from '@tabler/icons-react';
import { 
  LineChart, 
  Line, 
  PieChart, 
  Pie, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  Cell 
} from 'recharts';
import api from '../../services/api';
import * as XLSX from 'xlsx-js-style';

interface StatusLog {
  _id: string;
  timestamp: string;
  agentId: string;
  agentName: string;
  agentEmail: string;
  oldStatus: {
    id: string;
    name: string;
    color: string;
    type: string;
  };
  newStatus: {
    id: string;
    name: string;
    color: string;
    type: string;
  };
  duration: number;
  reason?: string;
  changedBy: {
    id: string;
    name: string;
    role: string;
  };
  source: string;
}

interface StatusStats {
  totalChanges: number;
  byAgent: { [key: string]: number };
  byStatus: { [key: string]: { count: number; totalDuration: number; color?: string } };
  byHour: number[];
  avgDuration: number;
  totalDuration: number;
}

interface StatusReportsProps {
  startDate: string;
  endDate: string;
}

const StatusReports: React.FC<StatusReportsProps> = ({ startDate, endDate }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [statusLogs, setStatusLogs] = useState<StatusLog[]>([]);
  const [statusStats, setStatusStats] = useState<StatusStats | null>(null);
  const [statusDurations, setStatusDurations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState('all');
  const [agents, setAgents] = useState<any[]>([]);

  useEffect(() => {
    loadAgents();
  }, []);

  useEffect(() => {
    loadData();
  }, [startDate, endDate, selectedAgent, activeTab]);

  const loadAgents = async () => {
    try {
      const response = await api.get('/users', {
        params: { role: 'agent' }
      });
      if (response.data.success) {
        setAgents(response.data.data || response.data.users || []);
      }
    } catch (error) {
      console.error('Error loading agents:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const params: any = { startDate, endDate };
      if (selectedAgent !== 'all') {
        params.agentId = selectedAgent;
      }

      // Tab 0: Status Change Logs
      if (activeTab === 0) {
        const [logsResponse, statsResponse] = await Promise.all([
          api.get('/reports/status-change-logs', { params }),
          api.get('/reports/status-change-stats', { params })
        ]);

        if (logsResponse.data.success) {
          setStatusLogs(logsResponse.data.data || []);
        }
        if (statsResponse.data.success) {
          setStatusStats(statsResponse.data.data);
        }
      }
      // Tab 1: Status Durations (Aggregated)
      else if (activeTab === 1) {
        const response = await api.get('/reports/agent-status-durations', { params });
        if (response.data.success) {
          setStatusDurations(response.data.data || []);
        }
      }
    } catch (error) {
      console.error('Error loading status data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds: number) => {
    if (!seconds) return '-';
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}s ${mins}d ${secs}sn`;
    }
    return `${mins}d ${secs}sn`;
  };

  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date.toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatPercentage = (value: number | undefined | null) => {
    if (value === undefined || value === null || isNaN(value)) return '0.0%';
    return `${value.toFixed(1)}%`;
  };

  const getStatusColor = (statusName: string) => {
    const lowerName = statusName.toLowerCase();
    if (lowerName.includes('online') || lowerName.includes('hazır')) return '#10b981';
    if (lowerName.includes('acv') || lowerName.includes('çağrı')) return '#3b82f6';
    if (lowerName.includes('break') || lowerName.includes('mola')) return '#f59e0b';
    if (lowerName.includes('offline') || lowerName.includes('çevrimdışı')) return '#6b7280';
    return '#8b5cf6';
  };

  const exportChangeLogsToExcel = async () => {
    try {
      // Tüm veriyi çek (export için)
      const params: any = { startDate, endDate, limit: 50000 };
      if (selectedAgent !== 'all') {
        params.agentId = selectedAgent;
      }

      const response = await api.get('/reports/status-change-logs', { params });
      const allLogs = response.data.success ? (response.data.data || []) : [];

      const exportData = allLogs.map((log: any, index: number) => ({
        'Sıra': index + 1,
        'Tarih/Saat': formatTimestamp(log.timestamp),
        'Agent': log.agentName,
        'Email': log.agentEmail,
        'Eski Statü': log.oldStatus.name,
        'Yeni Statü': log.newStatus.name,
        'Süre (sn)': log.duration,
        'Süre': formatDuration(log.duration),
        'Değiştiren': log.changedBy.name,
        'Rol': log.changedBy.role,
        'Sebep': log.reason || '-'
      }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    
    // Stil uygula
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
    
    // Header stilleri
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const address = XLSX.utils.encode_col(C) + "1";
      if (!ws[address]) continue;
      ws[address].s = {
        fill: { fgColor: { rgb: "EC4899" } },
        font: { bold: true, color: { rgb: "FFFFFF" } },
        alignment: { horizontal: "center", vertical: "center" }
      };
    }

    ws['!cols'] = [
      { wch: 6 },  // Sıra
      { wch: 20 }, // Tarih/Saat
      { wch: 20 }, // Agent
      { wch: 25 }, // Email
      { wch: 15 }, // Eski Statü
      { wch: 15 }, // Yeni Statü
      { wch: 10 }, // Süre (sn)
      { wch: 12 }, // Süre
      { wch: 20 }, // Değiştiren
      { wch: 12 }, // Rol
      { wch: 30 }  // Sebep
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Statü Değişim Logları');

    // Add statistics sheet
    if (statusStats) {
      const statsData = [
        ['Statü Değişim İstatistikleri', ''],
        ['', ''],
        ['Toplam Değişim:', statusStats.totalChanges],
        ['Ortalama Süre:', formatDuration(statusStats.avgDuration)],
        ['Toplam Süre:', formatDuration(statusStats.totalDuration)],
        ['', ''],
        ['Statüye Göre Dağılım:', ''],
        ['Statü', 'Değişim Sayısı', 'Toplam Süre']
      ];

      Object.entries(statusStats.byStatus).forEach(([status, data]) => {
        statsData.push([
          status,
          data.count.toString(),
          formatDuration(data.totalDuration)
        ]);
      });

      const statsWs = XLSX.utils.aoa_to_sheet(statsData);
      statsWs['!cols'] = [{ wch: 25 }, { wch: 15 }, { wch: 15 }];
      
      XLSX.utils.book_append_sheet(wb, statsWs, 'İstatistikler');
    }

    XLSX.writeFile(wb, `Statu_Degisim_Raporu_${startDate}_${endDate}.xlsx`);
    } catch (error) {
      console.error('Error exporting change logs:', error);
    }
  };

  const exportDurationsToExcel = () => {
    const exportData = statusDurations.map((agent, index) => {
      const row: any = {
        'Sıra': index + 1,
        'Agent': `${agent.firstName} ${agent.lastName}`,
        'Email': agent.email,
        'Toplam Online': formatDuration(agent.totalOnlineTime)
      };

      // Statü sürelerini ekle
      agent.statuses?.forEach((status: any) => {
        row[status.statusName] = formatDuration(status.duration);
        row[`${status.statusName} %`] = formatPercentage(status.percentage);
      });

      return row;
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    
    // Stil uygula
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
    
    // Header stilleri
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const address = XLSX.utils.encode_col(C) + "1";
      if (!ws[address]) continue;
      ws[address].s = {
        fill: { fgColor: { rgb: "EC4899" } },
        font: { bold: true, color: { rgb: "FFFFFF" } },
        alignment: { horizontal: "center", vertical: "center" }
      };
    }

    // Tüm hücreleri ortala
    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const address = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[address]) continue;
        if (!ws[address].s) ws[address].s = {};
        ws[address].s.alignment = { horizontal: "center", vertical: "center" };
      }
    }

    // Auto genişlik
    const colWidths = exportData.reduce((acc: any[], row: any) => {
      Object.keys(row).forEach((key, index) => {
        const length = String(row[key]).length;
        if (!acc[index] || acc[index] < length) {
          acc[index] = length + 2;
        }
      });
      return acc;
    }, []);

    ws['!cols'] = colWidths.map((w: number) => ({ wch: Math.max(w, 10) }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Statü Süreleri');
    XLSX.writeFile(wb, `Statu_Sureleri_Raporu_${startDate}_${endDate}.xlsx`);
  };

  // Tüm statüleri topla (for durations tab)
  const allStatuses = new Set<string>();
  statusDurations.forEach((agent: any) => {
    agent.statuses?.forEach((status: any) => {
      allStatuses.add(status.statusName);
    });
  });

  // Prepare chart data for change logs
  const pieChartData = statusStats
    ? Object.entries(statusStats.byStatus).map(([name, data]: [string, any]) => ({
        name,
        value: data.count,
        fill: data.color || getStatusColor(name)
      }))
    : [];

  const barChartData = statusStats
    ? Object.entries(statusStats.byStatus).map(([name, data]: [string, any]) => ({
        name: name.length > 15 ? name.substring(0, 15) + '...' : name,
        duration: Math.round(data.totalDuration / 60), // Convert to minutes
        count: data.count
      }))
    : [];

  const hourlyChartData = statusStats
    ? statusStats.byHour.map((count: number, hour: number) => ({
        hour: `${hour.toString().padStart(2, '0')}:00`,
        count
      }))
    : [];

  return (
    <Box>
      {/* Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="fullWidth"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 500
            }
          }}
        >
          <Tab label="📋 Statü Değişim Logları" />
          <Tab label="⏱️ Statü Süreleri (Toplam)" />
        </Tabs>
      </Card>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Agent Seç</InputLabel>
                <Select
                  value={selectedAgent}
                  onChange={(e) => setSelectedAgent(e.target.value)}
                  label="Agent Seç"
                >
                  <MenuItem value="all">Tüm Agentlar</MenuItem>
                  {agents.map(agent => (
                    <MenuItem key={agent._id} value={agent._id}>
                      {agent.firstName} {agent.lastName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 8 }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<IconRefresh size={18} />}
                  onClick={loadData}
                  disabled={loading}
                >
                  Yenile
                </Button>
                <Button
                  variant="contained"
                  startIcon={<IconDownload size={18} />}
                  onClick={activeTab === 0 ? exportChangeLogsToExcel : exportDurationsToExcel}
                  disabled={activeTab === 0 ? statusLogs.length === 0 : statusDurations.length === 0}
                >
                  Excel İndir
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tab 0: Status Change Logs */}
      {activeTab === 0 && (
        <>
          {/* Summary Cards */}
          {statusStats && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card sx={{ bgcolor: '#fdf2f8', border: '1px solid #fce7f3' }}>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Toplam Değişim
                    </Typography>
                    <Typography variant="h4" sx={{ color: '#ec4899', fontWeight: 'bold' }}>
                      {statusStats.totalChanges}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card sx={{ bgcolor: '#f0fdf4', border: '1px solid #dcfce7' }}>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Ortalama Süre
                    </Typography>
                    <Typography variant="h4" sx={{ color: '#10b981', fontWeight: 'bold' }}>
                      {formatDuration(statusStats.avgDuration)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card sx={{ bgcolor: '#eff6ff', border: '1px solid #dbeafe' }}>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Toplam Agent
                    </Typography>
                    <Typography variant="h4" sx={{ color: '#3b82f6', fontWeight: 'bold' }}>
                      {Object.keys(statusStats.byAgent).length}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card sx={{ bgcolor: '#fef3c7', border: '1px solid #fde68a' }}>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Farklı Statü
                    </Typography>
                    <Typography variant="h4" sx={{ color: '#f59e0b', fontWeight: 'bold' }}>
                      {Object.keys(statusStats.byStatus).length}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Charts */}
          {statusStats && (
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>Saatlik Değişim Dağılımı</Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={hourlyChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="hour" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="count" stroke="#ec4899" name="Değişim Sayısı" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>Statü Dağılımı</Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={pieChartData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }: any) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                          outerRadius={80}
                          dataKey="value"
                        >
                          {pieChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>Statülere Göre Toplam Süre (Dakika)</Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={barChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="duration" fill="#10b981" name="Toplam Süre (dk)" />
                        <Bar dataKey="count" fill="#3b82f6" name="Değişim Sayısı" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Change Logs Table */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Statü Değişim Geçmişi ({statusLogs.length})
              </Typography>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>Tarih/Saat</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>Agent</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', textAlign: 'center' }}>Eski Statü</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', textAlign: 'center' }}></TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', textAlign: 'center' }}>Yeni Statü</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', textAlign: 'center' }}>Süre</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>Değiştiren</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>Sebep</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {statusLogs.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center">
                            <Typography color="text.secondary" sx={{ py: 2 }}>
                              Seçili tarih aralığında statü değişimi bulunamadı
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        statusLogs.map((log) => (
                          <TableRow key={log._id} hover>
                            <TableCell>
                              <Typography variant="body2">
                                {formatTimestamp(log.timestamp)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {log.agentName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {log.agentEmail}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={log.oldStatus.name}
                                size="small"
                                sx={{
                                  bgcolor: log.oldStatus.color || getStatusColor(log.oldStatus.name),
                                  color: '#fff',
                                  fontWeight: 500
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <IconArrowRight size={18} color="#9ca3af" />
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={log.newStatus.name}
                                size="small"
                                sx={{
                                  bgcolor: log.newStatus.color || getStatusColor(log.newStatus.name),
                                  color: '#fff',
                                  fontWeight: 500
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                                <IconClock size={14} />
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {formatDuration(log.duration)}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {log.changedBy.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {log.changedBy.role === 'supervisor' ? '👨‍💼 Supervisor' : 
                                   log.changedBy.role === 'admin' ? '👑 Admin' : '👤 Agent'}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color={log.reason ? 'textPrimary' : 'textSecondary'}>
                                {log.reason || '-'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Tab 1: Status Durations (Aggregated) */}
      {activeTab === 1 && (
        <>
          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Card sx={{ bgcolor: '#fdf2f8', border: '1px solid #fce7f3' }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Toplam Agent
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#ec4899', fontWeight: 'bold' }}>
                    {statusDurations.length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Card sx={{ bgcolor: '#f0fdf4', border: '1px solid #dcfce7' }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Toplam Online Süre
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#10b981', fontWeight: 'bold' }}>
                    {formatDuration(statusDurations.reduce((sum, a) => sum + (a.totalOnlineTime || 0), 0))}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Card sx={{ bgcolor: '#eff6ff', border: '1px solid #dbeafe' }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Farklı Statü Sayısı
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#3b82f6', fontWeight: 'bold' }}>
                    {allStatuses.size}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Status Durations Table */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Agent Statü Dağılımı</Typography>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', minWidth: 200 }}>Agent</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', textAlign: 'center' }}>Toplam Online</TableCell>
                        {Array.from(allStatuses).map(statusName => (
                          <TableCell 
                            key={statusName} 
                            sx={{ 
                              fontWeight: 'bold', 
                              bgcolor: '#f8fafc', 
                              textAlign: 'center',
                              minWidth: 150
                            }}
                          >
                            <Chip
                              label={statusName}
                              size="small"
                              sx={{
                                bgcolor: getStatusColor(statusName),
                                color: 'white',
                                fontWeight: 'bold'
                              }}
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {statusDurations.map((agent) => (
                        <TableRow key={agent._id} hover>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {agent.firstName} {agent.lastName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {agent.email}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                              <IconClock size={16} />
                              <Typography sx={{ fontWeight: 500 }}>
                                {formatDuration(agent.totalOnlineTime)}
                              </Typography>
                            </Box>
                          </TableCell>
                          {Array.from(allStatuses).map(statusName => {
                            const statusData = agent.statuses?.find((s: any) => s.statusName === statusName);
                            return (
                              <TableCell key={statusName} align="center">
                                {statusData ? (
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {formatDuration(statusData.duration)}
                                    </Typography>
                                    <Chip
                                      label={formatPercentage(statusData.percentage)}
                                      size="small"
                                      sx={{
                                        mt: 0.5,
                                        fontSize: '0.7rem',
                                        height: 20,
                                        bgcolor: `${getStatusColor(statusName)}20`,
                                        color: getStatusColor(statusName),
                                        fontWeight: 'bold'
                                      }}
                                    />
                                  </Box>
                                ) : (
                                  <Typography color="text.secondary">-</Typography>
                                )}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default StatusReports;
